// ============================================================
// MAP FILES - a built map you can carry out of the session.
//
// The map library lives at `sessions/{id}/shared/battlefieldLibrary`, so
// a new session starts empty and everything built in the old one is
// unreachable. Characters solved this years ago and did not need a
// server to: they travel as FILES. Maps do the same.
//
// ONE FORMAT FOR ONE MAP AND FOR MANY. A single map is a file with one
// entry; a set is a file with several. A separate single-map format
// would be a second thing to version, a second thing to sniff on import
// and a second thing to get wrong, in exchange for nothing.
//
// Terrain rides through the same `packGrid`/`unpackGrid` pair the
// Firestore documents use, so the file inherits the storage format's
// compactness AND its tolerance of older shapes rather than inventing a
// third representation.
//
// Pure. Zero React dependency.
// ============================================================
import { packGrid, unpackGrid } from './gridCodec';
import { oneSurfacePerCell } from './zones';
import type { Terrain } from './presets';

/** What every map file says it is. Anything else is somebody else's file. */
export const MAP_FILE_KIND = 'deathwatch-maps';

/**
 * Bumped only for a change a reader cannot absorb silently. The grid's
 * own shape is versioned separately by `gridCodec`, which is why loading
 * a map built before packing existed still works.
 */
const MAP_FILE_VERSION = 1;

export interface MapEntry {
  name: string;
  terrain: Terrain;
}

/**
 * A map may carry a suggested deployment marker so an authored map can
 * propose where a fight starts. It is applied only when the session has
 * no marker of its own - see `loadMap` - because a marker somebody
 * planted must not be overwritten by opening a map.
 */

export interface MapFile {
  kind: typeof MAP_FILE_KIND;
  version: number;
  exportedAt?: string;
  maps: MapEntry[];
}

/** A library entry as the app holds it, before packing. */
interface LibraryEntry {
  name: string;
  terrain: Terrain;
}

/**
 * Library entries → the file to hand somebody.
 *
 * `exportedAt` is passed in rather than stamped here: this module is
 * pure, and a function that reads the clock cannot be tested without
 * freezing time.
 */
export function toMapFile(entries: LibraryEntry[], exportedAt?: string): MapFile {
  return {
    kind: MAP_FILE_KIND,
    version: MAP_FILE_VERSION,
    ...(exportedAt ? { exportedAt } : {}),
    maps: entries
      .filter((e) => e && e.name && e.terrain)
      .map((e) => ({ name: e.name, terrain: packTerrain(e.terrain) })),
  };
}

export interface ReadResult {
  maps: MapEntry[];
  /** Why nothing could be read, in words a person can act on. */
  error: string | null;
}

/**
 * A parsed file → the entries to merge, or a reason it cannot be.
 *
 * Never throws. An import is a person handing the app a file they may
 * have picked by mistake, and the answer to the wrong file is a sentence,
 * not a stack trace behind an error boundary.
 */
export function readMapFile(data: unknown): ReadResult {
  if (!data || typeof data !== 'object') {
    return { maps: [], error: 'That file does not contain a map.' };
  }
  const file = data as Partial<MapFile>;
  if (file.kind !== MAP_FILE_KIND) {
    return {
      maps: [],
      error: 'That is not a Deathwatch map file. A session file goes through Import Session instead.',
    };
  }
  if (!Array.isArray(file.maps) || file.maps.length === 0) {
    return { maps: [], error: 'That map file is empty.' };
  }

  const maps = file.maps
    .filter((m) => m && typeof m.name === 'string' && m.name && m.terrain)
    .map((m) => ({ name: m.name, terrain: unpackTerrain(m.terrain) }));

  if (maps.length === 0) return { maps: [], error: 'That map file has nothing readable in it.' };
  return { maps, error: null };
}

/**
 * Merge imported maps into a library.
 *
 * Same rule `saveMap` already follows: a name is a name, and importing
 * "Docks" over "Docks" replaces it. A library of Docks, Docks (2) and
 * Docks (3) is nobody's idea of a library, and the alternative - refusing
 * the import - makes updating a map you have already shared impossible.
 *
 * Incoming order wins for the entries it carries; everything else keeps
 * its place.
 */
export function mergeIntoLibrary(library: LibraryEntry[], incoming: MapEntry[]): LibraryEntry[] {
  const names = new Set(incoming.map((m) => m.name));
  return [...library.filter((e) => !names.has(e.name)), ...incoming];
}

// ── Terrain packing, shared with the Firestore documents ──────

export function packTerrain(terrain: Terrain): Terrain {
  const t = terrain as Terrain & { voxels?: unknown };
  if (!t?.voxels) return terrain;
  return { ...terrain, voxels: packGrid(t.voxels as never) } as Terrain;
}

/** A map file's terrain as the app holds it: the grid unpacked, one surface per cell (zones.ts). */
export function unpackTerrain(terrain: Terrain): Terrain {
  if (!terrain) return terrain;
  const zones = oneSurfacePerCell(terrain.zones);
  const t = (zones === terrain.zones ? terrain : { ...terrain, zones }) as Terrain & { voxels?: unknown };
  if (!t.voxels) return t;
  return { ...t, voxels: unpackGrid(t.voxels as never) } as Terrain;
}
